import { Badge } from "@/components/ui/badge";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssets } from "@/hooks/use-assets";
import { ASSET_CATEGORIES, assetCategoryPath, countUniqueAssetsByTopLevel, getGroupLabel } from "@/lib/asset-utils";
import { cn } from "@/lib/utils";
import { List } from "lucide-react";
import { NavLink, useLocation } from "react-router";

export function NavAssets() {
  const assetsQuery = useAssets();
  const assets = assetsQuery.data?.assets ?? [];
  const isLoading = assetsQuery.isLoading;
  const isInitialError = assetsQuery.isError && !assetsQuery.data;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assetCounts = countUniqueAssetsByTopLevel(assets);
  const hasAssets = assets.length > 0;
  const isListingActive = location.pathname === "/assets";

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <SidebarMenuItem>
            {isLoading || !hasAssets ? (
              <SidebarMenuButton tooltip="Assets" disabled>
                <List />
                <span>Assets</span>
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton asChild tooltip="Assets" isActive={isListingActive}>
                <NavLink to="/assets">
                  <List />
                  <span>Assets</span>
                </NavLink>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>

          {isLoading && (
            <div className="ml-4 mt-1 space-y-2">
              {ASSET_CATEGORIES.map((category) => (
                <Skeleton key={category.key} className="h-8 w-full" />
              ))}
            </div>
          )}

          {!isLoading && isInitialError && (
            <div role="alert" className="ml-4 mt-1 px-2 py-3 text-sm text-muted-foreground">
              Unable to load assets
            </div>
          )}

          {!isLoading && !isInitialError && !hasAssets && (
            <div className="ml-4 mt-1 px-2 py-3 text-sm text-muted-foreground">No assets available</div>
          )}

          {!isLoading && hasAssets && (
            <div className="ml-4 mt-1 space-y-1">
              {ASSET_CATEGORIES.map((category) => {
                const label = getGroupLabel(category.key);
                const count = category.count(assetCounts);
                const isActive =
                  isListingActive &&
                  searchParams.get("group") === category.group &&
                  searchParams.get("subgroup") === category.subgroup;
                const buttonClassName = cn(
                  "w-full justify-between text-sm",
                  isActive && "bg-accent/80 font-semibold hover:bg-accent",
                );
                const content = (
                  <>
                    <span className={cn(count === 0 && "opacity-50")}>{label}</span>
                    <Badge variant="outline" className="ml-auto">
                      {count}
                    </Badge>
                  </>
                );
                return (
                  <SidebarMenuItem key={category.key}>
                    {count === 0 ? (
                      <SidebarMenuButton tooltip={`No ${label} assets available`} disabled className={buttonClassName}>
                        {content}
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton asChild tooltip={label} isActive={isActive} className={buttonClassName}>
                        <NavLink to={assetCategoryPath(category)}>{content}</NavLink>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </div>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
